import moment from "moment";
import constants from "../../../shared/constants";
import errorConstants from "../../../shared/constants/error";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import PlayerConstants from "../../../shared/constants/player";
import { ANALYTICS_EVENT_TYPES } from "../../../shared/constants/analytics";
import middleware from "../../../shared/middleware";
import { getRandomInt } from "../../../shared/utils";
import { loadRecordingProgramDetail } from "../../../pages/PlayerPage/state/actions";
import {
  toggleSpinningLoaderAction,
  showToastNotification,
  showModalPopup,
  setVideoPlaying,
} from "../../../App/state/actions";
import { getAutoGeneratedObject, getAutogeneratedEpisodeString } from "../../../shared/utils/index";
const { bookmarkAVSVideo, fetchVideoUrl } = middleware;

const { VOD, LIVE, RECORDING } = PlayerConstants.PLAYER_TYPE;
const { PLAYBACK_TYPES, PLAYBACK_FORWARD_BUFFER } = PlayerConstants;
const { PAGE_CONTENT_ITEM_TYPES, MODAL_TYPES, IMAGES } = constants;
const { ERROR_TYPES } = errorConstants;

export const usePlayerActionUtils = () => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const toggleSpinningLoader = (isVisible, playerLoaderClassName = null) => {
    dispatch(toggleSpinningLoaderAction(isVisible, playerLoaderClassName));
  };

  const displayToastNotification = (translationKey) => {
    dispatch(showToastNotification(translate(translationKey)));
  };

  const displayModalPopup = (modalType, modalContent) => {
    dispatch(showModalPopup(modalType, modalContent));
  };

  const updateVideoPlayingState = (playingStateObj) => {
    dispatch(setVideoPlaying(playingStateObj));
  };

  const handleStopContent = ({
    appProvider,
    activeContentId,
    hasCalledStopContent,
    playbackType,
    playbackTimeDelta,
    buildBookmarkCallback = null,
    playerLoaderClassName,
    isRestartedStream,
    isLookbackStream,
    liveProgramDetails,
    setIsReadyToChangeStreams,
    exitLookbackMode,
    changeLookbackStream,
    switchLive,
    type,
    updateSeasonEntitlements,
    startingOverStream,
    isClosingPlayer,
    playerError,
    newContentId,
    recordingsList,
    isCPVRBookmarksEnabled,
  }) => {
    if (appProvider && activeContentId.current && !hasCalledStopContent.current) {
      hasCalledStopContent.current = true;
      const contentId = activeContentId.current;
      activeContentId.current = newContentId;
      if (playbackType !== PLAYBACK_TYPES.TRAILER) {
        const requestBody = {
          deltaThreshold: playbackTimeDelta.current,
          bookmark: buildBookmarkCallback !== null ? buildBookmarkCallback() : null,
        };

        toggleSpinningLoader(true, playerLoaderClassName);

        if ((isRestartedStream || isLookbackStream) && liveProgramDetails) {
          const requestBody = {
            deltaThreshold: null,
          };
          return bookmarkAVSVideo(
            appProvider,
            liveProgramDetails.channel?.id,
            PAGE_CONTENT_ITEM_TYPES.program,
            requestBody
          ).finally(() => {
            // Allow loading a different stream when the stop content call has completed
            setIsReadyToChangeStreams && setIsReadyToChangeStreams(true);
            if (!playerError && !startingOverStream.current && !isClosingPlayer.current) {
              if (isLookbackStream && !exitLookbackMode && changeLookbackStream !== null) {
                changeLookbackStream(liveProgramDetails);
              } else {
                switchLive();
              }
            }
            hasCalledStopContent.current = true;
          });
        } else {
          // NOTE: Bookmarking also does stopContent AKA reducing concurrent stream count
          if (type === RECORDING) {
            return (
              requestBody?.bookmark &&
              bookmarkAVSVideo(appProvider, contentId, type, requestBody)
                .then((bookmarkResponse) => {
                  if (bookmarkResponse && isCPVRBookmarksEnabled) {
                    dispatch(loadRecordingProgramDetail(appProvider, contentId, type, null, recordingsList, true));
                  }
                })
                .catch((err) => {
                  console.error(err);
                })
                .finally(() => {
                  hasCalledStopContent.current = false;
                  // Allow loading a different stream when the stop content call has completed
                  setIsReadyToChangeStreams && setIsReadyToChangeStreams(true);
                  if (type === VOD && playbackType === PLAYBACK_TYPES.EPISODE) updateSeasonEntitlements();
                  hasCalledStopContent.current = true;
                })
            );
          } else {
            return bookmarkAVSVideo(appProvider, contentId, type, requestBody)
              .catch((err) => {
                console.error(err);
              })
              .finally(() => {
                hasCalledStopContent.current = false;
                // Allow loading a different stream when the stop content call has completed
                setIsReadyToChangeStreams && setIsReadyToChangeStreams(true);
                if (type === VOD && playbackType === PLAYBACK_TYPES.EPISODE) updateSeasonEntitlements();
                hasCalledStopContent.current = true;
              });
          }
        }
      }
    }
    return Promise.resolve();
  };

  const managePlaybackDeltaTimer = (playbackDeltaTimer, playbackTimeDelta, shouldStart) => {
    if (shouldStart) {
      if (!playbackDeltaTimer.current) {
        playbackDeltaTimer.current = setInterval(() => {
          playbackTimeDelta.current += 1;
        }, 1000);
      }
    } else {
      if (playbackDeltaTimer.current) {
        clearInterval(playbackDeltaTimer.current);
        playbackDeltaTimer.current = null;
      }
    }
  };

  return {
    toggleSpinningLoader,
    displayToastNotification,
    displayModalPopup,
    handleStopContent,
    managePlaybackDeltaTimer,
    updateVideoPlayingState,
  };
};

export const initLiveProgressStyle = (type, showLiveProgress) => {
  if (type === LIVE && showLiveProgress.current === false) {
    showLiveProgress.current = true;
  }
};

// Code relaying to cpix and removal of pssh boxes
export const extractAsciiString = (dataView, position, length) => {
  const to = position + length;
  let str = "";
  for (; position < to; position++) {
    str += String.fromCharCode(dataView.getUint8(position));
  }
  return str;
};

// Code relaying to cpix and removal of pssh boxes
export const parseBoxes = (dataView) => {
  const boxes = [];
  let offset = 0;

  while (offset + 8 < dataView.byteLength) {
    const size = dataView.getUint32(offset);
    const type = extractAsciiString(dataView, offset + 4, 4);
    const data = new DataView(dataView.buffer, dataView.byteOffset + offset, size); // including box size and type (8 bytes)
    const payload = new DataView(dataView.buffer, dataView.byteOffset + offset + 8, size - 8);
    boxes.push({ type, data, payload });
    offset += size;
  }

  return boxes;
};

export const isOnDemandRestartButtonVisible = (playbackType, isOnDemandType, isDynamicRecordingType) => {
  return isOnDemandType && !isDynamicRecordingType && playbackType !== PLAYBACK_TYPES.TRAILER;
};

export const isLiveRestartButtonVisible = (
  type,
  isRestartedStream,
  isStartOverAllowed,
  programCurrentlyAiring,
  isCatchUpAllowed
) => {
  if (type === LIVE) {
    const canStartOver = !isRestartedStream && isStartOverAllowed && programCurrentlyAiring;
    const canCatchUp = isCatchUpAllowed;
    return canStartOver || canCatchUp;
  }
  return false;
};

export const generateEpisodeInfoString = (nextProgram) => {
  const uaEpisodeObject = getAutoGeneratedObject(nextProgram.metadata);
  return getAutogeneratedEpisodeString(uaEpisodeObject, nextProgram.metadata);
};

export const handleRestartedJumpToLive = (handleAVSStopContent, landTime, switchLive) => {
  handleAVSStopContent();
  landTime.current = null;
  switchLive();
};

export const showPurchaseModal = (
  liveProgramDetails,
  purchaseCallback,
  ppvPurchasePackage,
  displayModalPopup,
  getAVSPosterArtImage
) => {
  if (liveProgramDetails) {
    const modalContent = {
      channel: liveProgramDetails.channel,
      itemMetadata: liveProgramDetails.metadata,
      itemImage: getAVSPosterArtImage(liveProgramDetails.metadata, IMAGES.ASPECT_RATIOS.DIM_2x3),
      purchaseCallback,
      purchasePackage: { ...ppvPurchasePackage, programId: liveProgramDetails.metadata.contentId },
    };
    displayModalPopup(MODAL_TYPES.PURCHASE, modalContent);
  }
};

export const getCurrentTimestamp = () => {
  return moment().valueOf();
};

export const getLiveBufferPercentage = (currentTime, landTime, startTime, endTime) => {
  return ((currentTime - PLAYBACK_FORWARD_BUFFER * 1000 - landTime) / (endTime - startTime)) * 100;
};

export const updateLiveProgramAiringStatus = (
  currentTime,
  endTime,
  programCurrentlyAiring,
  setProgramCurrentlyAiring
) => {
  if (currentTime - PLAYBACK_FORWARD_BUFFER * 1000 >= endTime) {
    if (programCurrentlyAiring) {
      setProgramCurrentlyAiring(false);
    }
    return false;
  } else {
    if (!programCurrentlyAiring) {
      setProgramCurrentlyAiring(true);
    }
    return true;
  }
};

export const calculateNonPltvStreamProgress = (startTime, endTime) => {
  return ((getCurrentTimestamp() - startTime) / (endTime - startTime)) * 100;
};

export const stopAutoPlayHandler = (autoPlayTimer, setShowNextEpisodeCard, signalUserActivity) => {
  if (autoPlayTimer.current) {
    clearTimeout(autoPlayTimer.current);
    autoPlayTimer.current = null;
  }
  setShowNextEpisodeCard(false);
  signalUserActivity();
};

export const calculateVodPos = (videoDuration, progress) => {
  return Math.floor((videoDuration / 100) * progress);
};

export const calculateRecordingPos = (recordingDuration, progress, recordingStartDeltaTime) => {
  return Math.floor((recordingDuration / 100) * progress) + recordingStartDeltaTime;
};

export const updateOnDemandProgress = (progress, setProgressStyle, updateVodProgress) => {
  setProgressStyle(progress);
  if (updateVodProgress) {
    updateVodProgress(progress);
  }
};

export const handleOnDemandProgressFastForward = (
  progress,
  videoPos,
  isFFEnabled,
  setProgressStyle,
  updateVodProgress,
  seekPlayback,
  trackConvivaAppTrackerEvent,
  getMediaNodeMetaData,
  onPlaybackStateChange,
  translate,
  videoStarted
) => {
  if (isFFEnabled) {
    updateOnDemandProgress(progress, setProgressStyle, updateVodProgress);
    seekPlayback(videoPos, false);
    trackConvivaAppTrackerEvent(ANALYTICS_EVENT_TYPES.FAST_FORWARD, getMediaNodeMetaData);
  } else {
    if (videoStarted) {
      onPlaybackStateChange({
        errorType: ERROR_TYPES.ERROR,
        message: translate("message_fast_forward_disabled"),
        code: 226,
      });
    }
  }
};

export const handleOnDemandProgressRewind = (
  progress,
  videoPos,
  isRWEnabled,
  setProgressStyle,
  updateVodProgress,
  seekPlayback,
  trackConvivaAppTrackerEvent,
  getMediaNodeMetaData,
  onPlaybackStateChange,
  translate,
  videoStarted
) => {
  if (isRWEnabled) {
    updateOnDemandProgress(progress, setProgressStyle, updateVodProgress);
    seekPlayback(videoPos, false);
    trackConvivaAppTrackerEvent(ANALYTICS_EVENT_TYPES.REWIND, getMediaNodeMetaData);
  } else {
    if (videoStarted) {
      onPlaybackStateChange({
        errorType: ERROR_TYPES.ERROR,
        message: translate("message_rewind_disabled"),
        code: 226,
      });
    }
  }
};

export const scheduleRetry = (retryTimer, retryTsValue, isPlaybackRetry, performRetry) => {
  const shouldRetry =
    retryTsValue.current === undefined
      ? true
      : moment().valueOf() - retryTsValue.current > isPlaybackRetry?.retry_threshold_ms;
  if (retryTimer.current === null && shouldRetry) {
    const timerVal = getRandomInt(isPlaybackRetry?.start_offset_ms, isPlaybackRetry?.end_offset_ms);
    retryTsValue.current = moment().valueOf();
    retryTimer.current = setTimeout(performRetry, timerVal);
  }
};

export const handleRetry = async (
  appProvider,
  contentType,
  currentContentId,
  assetId,
  parentalPin,
  retryTimer,
  toggleSpinningLoader,
  initializePlayer,
  createRetryTimer
) => {
  const response = await fetchVideoUrl(appProvider, contentType, currentContentId, assetId, parentalPin, true);
  retryTimer.current = null;
  if (response.src || response.token) {
    toggleSpinningLoader(false);
    initializePlayer(response.src || null, response.token || null);
  } else {
    createRetryTimer();
  }
};

export const shouldRetryByStream = (isLive, isVOD, isFast, isRestart, isCPVR, isCatchup, featureToggles) => {
  const { isVODDisabled, isLIVEDisabled, isFASTDisabled, isRestartDisabled, isCatchUpDisabled, isCPVRDisabled } =
    featureToggles;

  if (
    (isLive && isLIVEDisabled) ||
    (isVOD && isVODDisabled) ||
    (isFast && isFASTDisabled) ||
    (isRestart && isRestartDisabled) ||
    (isCPVR && isCPVRDisabled) ||
    (isCatchup && isCatchUpDisabled)
  ) {
    return false;
  }
  return true;
};
